import { useEffect, useState } from 'react';
import { Shield, MessageSquare, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';

const TOKEN_KEY = 'ggm_portal_token';

// Read whatever stale token we still have lying around — sessionStorage,
// then URL ?token (in case the user opened a fresh expired link directly).
function readStaleToken(): string | null {
  try {
    const stored = (localStorage.getItem(TOKEN_KEY) ?? sessionStorage.getItem(TOKEN_KEY));
    if (stored) return stored;
  } catch { /* ignore */ }
  const params = new URLSearchParams(window.location.search);
  return params.get('token');
}

interface AuthConfig { oauth: boolean }

export function Expired() {
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [refreshState, setRefreshState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [staleToken] = useState<string | null>(() => readStaleToken());

  // Probe whether Discord OAuth is configured on this deployment.
  useEffect(() => {
    let live = true;
    fetch('/auth/config')
      .then(r => r.ok ? r.json() : { oauth: false })
      .then((c: AuthConfig) => { if (live) setConfig(c); })
      .catch(() => { if (live) setConfig({ oauth: false }); });
    return () => { live = false; };
  }, []);

  const refresh = async () => {
    if (!staleToken) {
      setRefreshState('error');
      setRefreshError('No previous token found in this browser. Use Login with Discord instead.');
      return;
    }
    setRefreshState('sending');
    setRefreshError(null);
    try {
      const res = await fetch('/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: staleToken }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || `${res.status} ${res.statusText}`);
      }
      // Server returned a fresh token directly — stash it and reload straight
      // back into the portal at the path the user was trying to reach.
      if (body.token) {
        try { localStorage.setItem(TOKEN_KEY, body.token); } catch { /* ignore */ }
        // Tiny visible delay so the success state isn't a flash, then reload
        // back to the original target (preserves any path in the URL).
        setRefreshState('sent');
        setTimeout(() => { window.location.reload(); }, 600);
        return;
      }
      // Fell back to DM only.
      setRefreshState('sent');
    } catch (err) {
      setRefreshState('error');
      setRefreshError((err as Error).message);
    }
  };

  const loginWithDiscord = () => {
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/auth/discord/start?returnTo=${returnTo}`;
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-8 px-4 py-10 text-center">
      <Shield className="h-14 w-14 text-violet-500/40" />

      <div className="max-w-md">
        <h1 className="font-display text-3xl font-bold uppercase tracking-wide text-white">GG Anvil</h1>
        <p className="mt-2 font-display text-sm font-bold uppercase tracking-wide text-violet-300">
          Tournament Portal
        </p>
        <p className="mt-3 font-mono text-sm text-iron-400">
          New here, or your link expired? Sign in with Discord to see your
          matches, join a competition, and track standings.
        </p>
      </div>

      <div className="flex w-full max-w-sm flex-col gap-3">
        {/* Refresh option */}
        <button
          onClick={refresh}
          disabled={refreshState === 'sending' || refreshState === 'sent'}
          className="group relative flex items-center justify-between gap-3 rounded-xl border border-emerald-700/60 bg-emerald-950/40 px-4 py-3 text-left transition-all hover:border-emerald-500 hover:bg-emerald-900/40 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <div className="min-w-0">
            <div className="font-display text-sm font-bold uppercase tracking-wide text-emerald-200">
              {refreshState === 'sent' ? 'Fresh link ready' : 'Resend my portal link'}
            </div>
            <div className="mt-0.5 font-mono text-[11px] text-emerald-400/70">
              {refreshState === 'sent'
                ? 'reloading you in…'
                : 'instant — uses your previous link to mint a new one'}
            </div>
          </div>
          {refreshState === 'sending' && <RefreshCw size={18} className="shrink-0 animate-spin text-emerald-300" />}
          {refreshState === 'sent' && <CheckCircle2 size={18} className="shrink-0 text-emerald-300" />}
          {refreshState === 'idle' && <RefreshCw size={18} className="shrink-0 text-emerald-400" />}
          {refreshState === 'error' && <AlertTriangle size={18} className="shrink-0 text-amber-400" />}
        </button>

        {/* OAuth option (hidden if not configured) */}
        {config?.oauth && (
          <button
            onClick={loginWithDiscord}
            className="group flex items-center justify-between gap-3 rounded-xl border border-indigo-700/60 bg-indigo-950/40 px-4 py-3 text-left transition-all hover:border-indigo-500 hover:bg-indigo-900/40"
          >
            <div className="min-w-0">
              <div className="font-display text-sm font-bold uppercase tracking-wide text-indigo-200">
                Login with Discord
              </div>
              <div className="mt-0.5 font-mono text-[11px] text-indigo-400/70">
                consent screen, then straight back here
              </div>
            </div>
            <MessageSquare size={18} className="shrink-0 text-indigo-400" />
          </button>
        )}

        {/* Fallback: bot DM via /ggportal */}
        <div className="rounded-xl border border-iron-800 bg-iron-950 px-4 py-2.5">
          <div className="font-mono text-[10px] uppercase tracking-wider text-iron-500">
            Or do it the old way
          </div>
          <p className="mt-1 text-xs text-iron-400">
            Run{' '}
            <code className="rounded bg-surface-800 px-1.5 py-0.5 text-violet-300">/ggportal</code>{' '}
            in Discord to get a fresh link by DM.
          </p>
        </div>
      </div>

      {refreshError && (
        <p className="max-w-md font-mono text-xs text-amber-300">
          {refreshError}
        </p>
      )}
    </div>
  );
}
